import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { RenewalBadge } from '../components/RenewalBadge';
import { Spinner } from '../components/Spinner';
import { RENEWAL_LOOKBACK_DAYS } from '../config';
import { listRenewals } from '../lib/catalogue';
import { addDaysIso, isoOrNull, todayIso } from '../lib/dates';
import { usePaged } from '../lib/hooks';

export function RenewalsPage() {
  const [from, setFrom] = useState(() => addDaysIso(todayIso(), -RENEWAL_LOOKBACK_DAYS));
  const load = useCallback((after: Parameters<typeof listRenewals>[1]) => listRenewals(from, after), [from]);
  const { loading, items, hasMore, error, loadingMore, loadMore } = usePaged(from, load);

  return (
    <section>
      <div className="page-head">
        <h1>Licence renewals</h1>
      </div>
      <p className="muted">
        Vehicles whose licence renewal date falls on or after the date below, soonest first. Past dates
        are overdue.
      </p>

      <div className="toolbar">
        <label htmlFor="from">Show renewals from</label>
        <input
          id="from"
          type="date"
          value={from}
          onChange={(e) => {
            const v = isoOrNull(e.target.value);
            if (v) setFrom(v);
          }}
        />
      </div>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 && !error ? (
        <p className="empty">No renewals from this date.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Renewal date</th>
                <th scope="col">Plate</th>
                <th scope="col">Vehicle</th>
                <th scope="col">Client</th>
                <th scope="col">Phone</th>
                <th scope="col">Email</th>
              </tr>
            </thead>
            <tbody>
              {items.map(({ vehicle, client }) => (
                <tr key={`${vehicle.clientId}/${vehicle.id}`}>
                  <td>
                    <RenewalBadge date={vehicle.licenseRenewalDate} />
                  </td>
                  <td className="plate">{vehicle.plate}</td>
                  <td>{[vehicle.make, vehicle.model].filter(Boolean).join(' ')}</td>
                  <td>
                    {client ? (
                      <Link to={`/clients/${client.id}`}>
                        {client.lastName}, {client.firstName}
                      </Link>
                    ) : (
                      <span className="muted">Unknown client</span>
                    )}
                  </td>
                  <td>{client && <a href={`tel:${client.phone}`}>{client.phone}</a>}</td>
                  <td>{client && <a href={`mailto:${client.email}`}>{client.email}</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="load-more">
          <button type="button" className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
